import { FC } from 'react';
import { useSingleCourse } from '@new/features/courses/contexts/SingleCourseProvider';
import { type Quiz, useGetQuizzesQuery } from '@teachur/http-client';
import { Loader } from '@new/common/ui/Loader/Loader';
import { PreviousAttempts } from './PreviousAttempts';
import { EmptyExamState } from './EmptyExamState';
import { RetryExamCard } from './RetryExamCard';
import { OverallProgress } from './OverallProgress';
import { EmptyState } from '@new/common/ui/EmptyState/EmptyState';
import { ExamModal } from './ExamModal/ExamModal';
import { ExamProvider } from '@new/features/courses/contexts/ExamProvider';

export const ExamContent = () => {
  return (
    <ExamProvider>
      <ExamContentData />
    </ExamProvider>
  );
};

const ExamContentData: FC = () => {
  const { course, activeStep } = useSingleCourse();
  const { data: quizzes, isLoading } = useGetQuizzesQuery({ courseId: course?.id });

  const attempts = quizzes?.data || ([] as Quiz[]);
  const hasAttempts = !!attempts.length;

  if (activeStep !== 'exam') return null;

  if (isLoading) {
    return <Loader />;
  }

  if (!course) return <EmptyState message="No course found." />;

  const isExamCompleted = course.quizCompleted;

  return (
    <div className="flex flex-col gap-6 rounded-2xl border-2 border-(--neutral-black-NB200) p-4 lg:p-8">
      {!hasAttempts ? (
        <EmptyExamState />
      ) : (
        <>
          <OverallProgress attempts={attempts} />
          {isExamCompleted && (
            <div className="flex flex-col rounded-[12px] gap-2 bg-(--success-S200) p-4 font-medium text-(--neutral-black-NB500) leading-tight">
              <span className="font-semibold text-(--success-S700) [font-size:var(--fs-p4)] lg:[font-size:var(--fs-p3)]!">
                Congratulations! You completed the exam successfully.
              </span>
              You answered all questions correctly and unlocked the next step.
            </div>
          )}
          <PreviousAttempts attempts={attempts} hideReset={isExamCompleted} />
          {!isExamCompleted && <RetryExamCard attempts={attempts} />}
        </>
      )}

      <ExamModal />
    </div>
  );
};
